/* === AgBottomSheet -- Anagogia Design System (C11) === */
import { useEffect, useRef, useId } from 'react';
import { createPortal } from 'react-dom';
import { useOverlayDismiss } from '../../../hooks/useOverlayDismiss';
import { useCalqueEchap } from '../utils/useCalqueEchap';
import styles from './AgBottomSheet.module.css';

interface AgBottomSheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** auto = hauteur du contenu (max 86 %) ; full = 86 % de la hauteur */
  height?: 'auto' | 'full';
  className?: string;
}

const HISTORY_MARK = { agBottomSheet: true };

/** Éléments focalisables retenus pour le piège de focus (spec § 3.1). */
const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Module-level : une seule traversée back() en attente à la fois. Évite qu'une sheet
// montée juste après la fermeture d'une autre (ex. menu « Actions » → confirmation de
// suppression) ne se fasse fermer par le popstate différé du back() de la précédente.
let pendingBack: Promise<void> | null = null;

function goBackOnce(): void {
  pendingBack = new Promise<void>(resolve => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      window.removeEventListener('popstate', finish);
      pendingBack = null;
      resolve();
    };
    window.addEventListener('popstate', finish);
    window.setTimeout(finish, 300); // filet de sécurité si aucun popstate n'arrive
    window.history.back();
  });
}

/**
 * Attend que la traversée d'historique laissée par la fermeture d'une sheet soit
 * retombée. **À attendre avant toute navigation qui suit une fermeture de sheet** :
 * sans ça, le `history.back()` encore en file rejoue le pop APRÈS le `navigate()`
 * et ramène l'utilisateur sur l'écran qu'il vient de quitter.
 */
export function settleSheetHistory(): Promise<void> {
  return pendingBack ?? Promise.resolve();
}

function SheetPortal({ onClose, title, children, footer, height = 'auto', className }: Omit<AgBottomSheetProps, 'open'>) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const closedByPop = useRef(false);

  // Les appelants passent souvent un onClose inline (identité nouvelle à chaque
  // rendu). Les effets d'Échap et d'historique ci-dessous doivent survivre à ces
  // rendus sans se redéclencher (sans quoi la cleanup de l'effet d'historique
  // rejouerait un vrai back() et laisserait un trou sans écouteur popstate) :
  // ils lisent toujours la dernière fonction via cette ref, jamais via une
  // dépendance d'effet.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Focus : la sheet le prend à l'ouverture et le rend à l'élément déclencheur
  // à la fermeture (spec § 3.1) — le lecteur d'écran et le clavier reviennent
  // là où l'utilisateur en était.
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    sheetRef.current?.focus();
    return () => { previous?.focus?.(); };
  }, []);

  // Échap : pile commune des calques ; le piège de Tab reste local (ci-dessous).
  useCalqueEchap(true, () => onCloseRef.current());

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const sheet = sheetRef.current;
      if (!sheet) return;
      const items = Array.from(sheet.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) { e.preventDefault(); sheet.focus(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      const outside = !active || !sheet.contains(active);
      if (e.shiftKey) {
        if (outside || active === first) { e.preventDefault(); last.focus(); }
      } else if (outside || active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- onCloseRef porte la dernière valeur

  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = original; };
  }, []);

  // Bouton Retour d'Android : une entrée d'historique est poussée à l'ouverture ;
  // popstate ferme la sheet. À la fermeture par un autre moyen, on retire l'entrée
  // via une traversée back() différée et sérialisée (goBackOnce) pour ne jamais
  // fermer par erreur une sheet montée entre-temps.
  // Dépendance [] volontaire : voir onCloseRef ci-dessus.
  useEffect(() => {
    let cancelled = false;
    let pushed = false;
    const onPop = () => { closedByPop.current = true; onCloseRef.current(); };
    (pendingBack ?? Promise.resolve()).then(() => {
      if (cancelled) return;
      window.history.pushState(HISTORY_MARK, '');
      pushed = true;
      window.addEventListener('popstate', onPop);
    });
    return () => {
      cancelled = true;
      window.removeEventListener('popstate', onPop);
      // Ne retirer l'entrée que si c'est bien la nôtre qui est au sommet : si
      // quelqu'un a navigué par-dessus la marque (replace ou push), un back()
      // ferait reculer d'un écran réel au lieu de nettoyer.
      const onTop = (window.history.state as { agBottomSheet?: boolean } | null)?.agBottomSheet === true;
      if (pushed && !closedByPop.current && onTop) goBackOnce();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- onCloseRef porte la dernière valeur

  // Le geste doit commencer *et* finir sur le fond : un appui dans la feuille
  // relâché sur le fond (sélection, glisser) ne doit pas la fermer.
  const backdropProps = useOverlayDismiss(onClose);

  return createPortal(
    <div className={styles.backdrop} data-testid="ag-sheet-backdrop" {...backdropProps}>
      <div
        ref={sheetRef}
        className={[styles.sheet, height === 'full' ? styles.full : '', className].filter(Boolean).join(' ')}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
      >
        <button type="button" className={styles.grabberButton} aria-label="Fermer" onClick={onClose}>
          <span className={styles.grabber} />
        </button>
        {title && <h2 id={titleId} className={styles.title}>{title}</h2>}
        <div className={styles.content}>{children}</div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Feuille basse tactile : remplaçant mobile d'AgModal / AgMenu. Portail dans body,
 * strate `--z-index-sheet` (= MODAL) — jamais deux sheets ouvertes.
 * Fermeture : backdrop, poignée, Échap, bouton Retour Android.
 * @category Overlays
 */
export function AgBottomSheet(props: AgBottomSheetProps) {
  if (!props.open) return null;
  return <SheetPortal {...props} />;
}

export type { AgBottomSheetProps };
export default AgBottomSheet;
